import { tWithKeyAttr as t } from "../../i18nHelper";
import { useGetMyHotlineQuestionnaireQuery } from "../../services/api/user.api";
import { getPrintDate } from "../../utils/utils";
import { useContactAccess } from "../../utils/contactAccess";
import styles from "./hotlineQuestionnaireCard.module.css";

/**
 * The returning-caller questionnaire, at the top of the patient's questionnaire tab.
 *
 * Always here, not only when something has gone chasing them. Anyone logged in already has a
 * treatment relationship and may fill it; the six-week window is the only thing that opens and
 * closes it. An earlier version derived this from an open requirement, which meant the
 * questionnaire existed for the patient only after a text had already been sent about it.
 *
 * Renders nothing at all when the company has not switched the rule on.
 */
function HotlineQuestionnaireCard() {
  const { questionnairesFill } = useContactAccess();
  const { data } = useGetMyHotlineQuestionnaireQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: !questionnairesFill,
  });

  const questionnaire = data?.questionnaire;
  if (!questionnaire) return null;

  // Closed. One line, and it is the only one worth printing: a thank-you for something they did
  // seven weeks ago is noise, and the date they can come back is the whole answer.
  //
  // A closed card always has both dates by construction: never having filled it leaves nothing in
  // force, which makes it open.
  if (!questionnaire.canFill) {
    return (
      <div className={`${styles.card} ${styles.card_done}`}>
        <div className={styles.name}>{questionnaire.name}</div>
        {questionnaire.availableFrom && (
          <p className={styles.reason}>
            {t("HOTLINE_QUESTIONNAIRE_CARD_AVAILABLE_FROM", {
              date: getPrintDate(new Date(questionnaire.availableFrom)),
              // i18next HTML-escapes interpolated values by default, and React then prints the
              // entities literally rather than decoding them -- a date came out as
              // "29&#x2F;10&#x2F;2026". Switched off for this call only: escaping is React's job
              // here, and turning it off globally would change every other translation at once.
              interpolation: { escapeValue: false },
            })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.name}>{questionnaire.name}</div>
      <p className={styles.reason}>{t("HOTLINE_QUESTIONNAIRE_CARD_DUE")}</p>

      {questionnaire.link ? (
        <a className={styles.action} href={questionnaire.link}>
          {t("HOTLINE_QUESTIONNAIRE_CARD_FILL")}
        </a>
      ) : (
        // The server could not build a link — no configured site, or no guid on the account.
        // Saying so beats a dead button: they were sent a working one by SMS.
        <p className={styles.fallback}>{t("HOTLINE_QUESTIONNAIRE_CARD_USE_SMS_LINK")}</p>
      )}
    </div>
  );
}

export default HotlineQuestionnaireCard;
